import React, { useEffect, useState } from "react";
import ToggleButton from "../UI/ToggleButton";
import StatsCards from "../UI/StatsCards";
import BarGraph from "../UI/BarGraph";
import SelectComponent from "../common/SelectComponent";
import styles from "../../_css/CostSection.module.css";
import { DataLoader } from "../common/ResponseLoader";
import { fetchCostData } from "../../requests";
import { deleteAuthToken } from "../../utils/helper";

const costLabels = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const STATS_DATA = [
  {
    title: "Total OpenAI Cost",
    content: "$213,400",
    info: "+8% Compared to previous 12 month",
    icon: "6",
    color: "dark",
  },
  {
    title: "Average Cost per User",
    content: "$41.67",
    info: "Calculated using 360 users in selected period",
    icon: "5",
    color: "blue",
  },
  {
    title: "Average Cost per Session",
    content: "$31.12",
    info: "Includes all chats and voice sessions",
    icon: "4",
    color: "green",
  },
];

const STATS_DATA_ELEVENLABS = [
  {
    title: "Total ElevenLabs Cost",
    content: "$213,400",
    info: "+8% Compared to previous 12 month",
    icon: "6",
    color: "dark",
  },
  {
    title: "Average Cost per User",
    content: "$41.67",
    info: "Calculated using 360 users in selected period",
    icon: "5",
    color: "blue",
  },
  {
    title: "Average Cost per Session",
    content: "$31.12",
    info: "Includes all chats and voice sessions",
    icon: "4",
    color: "green",
  },
];

function CostSection() {
  const [selected, setSelected] = useState("OPENAI COST");
  const [isLoading, setIsLoading] = useState(false);
  const [graphData, setGraphData] = useState({});
  const [eachGraphData, setEachGraphData] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [selectedStatsData, setSelectedStatsData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  useEffect(() => {
    getCostsGraphData();
  }, []);

  // Update graph data when provider changes (if year is already selected)
  useEffect(() => {
    if (selectedYear && graphData[selectedYear]) {
      if (selected === "OPENAI COST") {
        setEachGraphData(graphData[selectedYear].openai);
      } else if (selected === "ELEVENLAB COST") {
        setEachGraphData(graphData[selectedYear].elevenlabs);
      } else {
        setEachGraphData(graphData[selectedYear].aws_transcribe);
      }
    }
  }, [selected, selectedYear, graphData]);

  const getCostsGraphData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchCostData();
      setStatsData(response.data.cost_data.stats_data);
      setSelectedStatsData(response.data.cost_data.stats_data.openai);
      
      // Extract available years from monthly_data_all
      const years = Object.keys(response.data.monthly_data_all || {}).sort((a, b) => b - a);
      
      // Create options for dropdown
      const options = years.map(year => ({ label: year, value: year }));
      setYearOptions(options);
      
      // Set default to most recent year
      const defaultYear = years.length > 0 ? years[0] : null;
      
      setGraphData(response.data.monthly_data_all);
      
      // Set initial graph data for default year
      if (defaultYear && response.data.monthly_data_all[defaultYear]) {
        setSelectedYear(defaultYear);
        setEachGraphData(response.data.monthly_data_all[defaultYear].openai);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error in sending message:", error);
    }
    setIsLoading(false);
  };

  const handleToggleChange = (value) => {
    setSelected(value);
    if (selectedYear && graphData[selectedYear]) {
      if (value === "OPENAI COST") {
        setSelectedStatsData(statsData?.openai);
        setEachGraphData(graphData[selectedYear].openai);
      } else if (value === "ELEVENLAB COST") {
        setSelectedStatsData(statsData?.elevenlabs);
        setEachGraphData(graphData[selectedYear].elevenlabs);
      } else {
        setSelectedStatsData(statsData?.aws_transcribe);
        setEachGraphData(graphData[selectedYear].aws_transcribe);
      }
    }
  };

  const handleYearChange = (option) => {
    if (option && option.value) {
      const newYear = option.value;
      setSelectedYear(newYear);
      
      // Update graph data immediately when year changes
      if (graphData[newYear]) {
        if (selected === "OPENAI COST") {
          setEachGraphData(graphData[newYear].openai);
        } else if (selected === "ELEVENLAB COST") {
          setEachGraphData(graphData[newYear].elevenlabs);
        } else {
          setEachGraphData(graphData[newYear].aws_transcribe);
        }
      }
    }
  };

  const formatText = (text) => {
    if(text === "OPENAI COST") {
      return "OpenAI cost"
    }
    else if(text === "ELEVENLAB COST") {
      return "Elevenlab cost"
    }
    else {
      return "AWS Transcribe cost"
    }
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <ToggleButton
          options={["OPENAI COST", "ELEVENLAB COST", "AWS TRANSCRIBE"]}
          value={selected}
          onChange={handleToggleChange}
        />
        {yearOptions.length > 0 && (
          <div style={{ minWidth: "120px" }}>
            <SelectComponent
              options={yearOptions}
              placeholder="Select Year"
              handleValueChange={handleYearChange}
              value={selectedYear ? { label: selectedYear, value: selectedYear } : null}
              disabled={false}
              isMulti={false}
              isClearable={false}
            />
          </div>
        )}
      </div>
      {!isLoading ? (
        selectedStatsData.length > 0 && selectedYear && (
          <div key={`${selected}-${selectedYear}`} className={styles.costSectionRow}>
            <StatsCards data={selectedStatsData} variant="column" />
            <BarGraph
              title={`${formatText(selected)} per month - ${selectedYear}`}
              data={eachGraphData}
              labels={costLabels}
            />
          </div>
        )
      ) : (
        <DataLoader />
      )}
    </div>
  );
}

export default CostSection;
